/**
 * api.ts — API adapter for Remyx annotations
 *
 * Talks to the existing Flask endpoints:
 *   GET    /papers/:id/annotations
 *   POST   /papers/:id/annotations
 *   DELETE /papers/:id/annotations/:annId
 *
 * Converts between the library's ScaledPosition format and the
 * legacy xPct/yPct format stored in older annotations.
 */
import type { ScaledPosition } from "react-pdf-highlighter-extended";
import type {
  RemyxHighlight,
  RawAnnotation,
  AnnotationType,
} from "./types";
import { isScaledPosition, isLegacyPosition } from "./types";

declare const window: Window & {
  __PAPER__?: { id?: string };
  __EXPERIMENT_CONTEXT_ID__?: string | null;
  __PROJECT_ID__?: string | null;
  rmxTrack?: (event: string, props: Record<string, unknown>) => void;
};

/* ── Helpers ────────────────────────────────────────────────── */

// Default page size in PDF points (US letter) for legacy → scaled conversion.
// The library stores the actual page dimensions in ScaledPosition.width/height
// so new annotations will always have the real values.
const DEFAULT_PAGE_W = 612;
const DEFAULT_PAGE_H = 792;

function buildUrl(paperId: string, path: string): string {
  let url = `/papers/${paperId}${path}`;
  const expId = window.__EXPERIMENT_CONTEXT_ID__;
  if (expId && !url.includes("experiment_id")) {
    url += `${url.includes("?") ? "&" : "?"}experiment_id=${expId}`;
  }
  return url;
}

/* ── Format Conversion ──────────────────────────────────────── */

/**
 * Convert a raw annotation from the DB to the library's highlight format.
 * Supports both legacy (xPct/yPct) and new (ScaledPosition) position data.
 */
export function rawToHighlight(a: RawAnnotation): RemyxHighlight {
  let position: ScaledPosition;

  if (isScaledPosition(a.position_data)) {
    // Already new format
    position = a.position_data;
  } else if (isLegacyPosition(a.position_data)) {
    // Legacy xPct/yPct → ScaledPosition
    const scaled = a.position_data.rects.map((r) => ({
      x1: r.xPct * DEFAULT_PAGE_W,
      y1: r.yPct * DEFAULT_PAGE_H,
      x2: (r.xPct + r.wPct) * DEFAULT_PAGE_W,
      y2: (r.yPct + r.hPct) * DEFAULT_PAGE_H,
      width: DEFAULT_PAGE_W,
      height: DEFAULT_PAGE_H,
      pageNumber: r.page || a.page_number || 1,
    }));

    // boundingRect = union of all rects
    const br = scaled.length > 0 ? { ...scaled[0] } : {
      x1: 0, y1: 0, x2: 100, y2: 20,
      width: DEFAULT_PAGE_W, height: DEFAULT_PAGE_H,
      pageNumber: a.page_number || 1,
    };

    for (const r of scaled) {
      br.x1 = Math.min(br.x1, r.x1);
      br.y1 = Math.min(br.y1, r.y1);
      br.x2 = Math.max(br.x2, r.x2);
      br.y2 = Math.max(br.y2, r.y2);
    }

    position = {
      boundingRect: br,
      rects: scaled,
      pageNumber: br.pageNumber || a.page_number || 1,
    };
  } else {
    // No position data at all — place at top of the given page
    position = {
      boundingRect: {
        x1: 50, y1: 50, x2: 300, y2: 70,
        width: DEFAULT_PAGE_W, height: DEFAULT_PAGE_H,
        pageNumber: a.page_number || 1,
      },
      rects: [],
      pageNumber: a.page_number || 1,
    };
  }

  return {
    id: a.id,
    annotation_type: a.annotation_type || "highlight",
    note_text: a.note_text,
    author: a.author,
    experiment_id: a.experiment_id,
    position,
    content: { text: a.selected_text || "" },
  };
}

/* ── API Calls ──────────────────────────────────────────────── */

export interface AnnotationMode {
  mode: "personal" | "team";
}

export async function fetchAnnotations(
  paperId: string
): Promise<{ highlights: RemyxHighlight[]; mode: string }> {
  const res = await fetch(buildUrl(paperId, "/annotations"), {
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error(`Failed to load annotations: ${res.status}`);
  const data = await res.json();
  return {
    highlights: (data.annotations || []).map(rawToHighlight),
    mode: data.mode || "personal",
  };
}

export async function createAnnotation(
  paperId: string,
  payload: {
    annotation_type: AnnotationType;
    selected_text: string;
    note_text?: string | null;
    page_number: number;
    position_data: ScaledPosition;
  }
): Promise<RawAnnotation | null> {
  const expId = window.__EXPERIMENT_CONTEXT_ID__;
  const body: Record<string, unknown> = { ...payload };
  if (expId) body.experiment_id = expId;

  const res = await fetch(buildUrl(paperId, "/annotations"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).error || `HTTP ${res.status}`);
  }

  const data = await res.json();

  // PostHog tracking
  if (window.rmxTrack) {
    window.rmxTrack("annotation_created", {
      paper_id: paperId,
      annotation_type: payload.annotation_type,
    });
  }

  return data.annotation;
}

export async function deleteAnnotation(
  paperId: string,
  annotationId: string
): Promise<void> {
  const res = await fetch(buildUrl(paperId, `/annotations/${annotationId}`), {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok && res.status !== 204) {
    throw new Error(`Delete failed: ${res.status}`);
  }
}
